import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import {
  ENABLED,
  MEASUREMENT_ID,
  CONSENT_KEY,
  DISMISSED_KEY,
  AnalyticsWindow,
  Consent,
  UseAnalyticsResult,
} from '../types';
import { cleanUrl } from '../utils/cleanUrl';
import { disableAnalytics } from '../utils/disableAnalytics';
import {
  persistConsent,
  readConsent,
  isBannerDismissed,
  persistBannerDismissal,
} from '../utils/cookieConsent';

export default function useAnalytics(): UseAnalyticsResult {
  const router = useRouter();
  const [consent, setConsent] = useState<Consent>(null);
  const [hydrated, setHydrated] = useState(false);
  const [open, setOpen] = useState(false);
  const lastPage = useRef('');
  const previousPage = useRef('');

  useEffect(() => {
    const savedConsent = readConsent();
    setConsent(savedConsent);
    setOpen(savedConsent === null && !isBannerDismissed());
    setHydrated(true);

    const syncConsent = (event: StorageEvent) => {
      if (event.key === DISMISSED_KEY) {
        if (event.newValue !== null) setOpen(false);
        return;
      }
      if (event.key !== CONSENT_KEY && event.key !== null) return;
      const next = readConsent();
      if (next !== 'accepted') disableAnalytics();
      setConsent(next);
      setOpen(next === null && !isBannerDismissed());
    };

    window.addEventListener('storage', syncConsent);
    return () => window.removeEventListener('storage', syncConsent);
  }, []);

  useEffect(() => {
    if (!ENABLED || consent !== 'accepted' || !router.isReady) return;

    const analytics = window as AnalyticsWindow;
    analytics[`ga-disable-${MEASUREMENT_ID}`] = false;
    analytics.dataLayer = analytics.dataLayer || [];
    analytics.gtag =
      analytics.gtag ||
      function () {
        // eslint-disable-next-line prefer-rest-params
        analytics.dataLayer?.push(arguments);
      };

    if (!analytics.delpsAnalyticsInitialized) {
      analytics.gtag('js', new Date());
      analytics.gtag('config', MEASUREMENT_ID, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        page_location: cleanUrl(window.location.href),
        page_referrer: cleanUrl(document.referrer),
        page_title: document.title || 'Delps Transportes',
      });
      analytics.delpsAnalyticsInitialized = true;
    }

    const trackPage = () => {
      if (analytics[`ga-disable-${MEASUREMENT_ID}`]) return;

      const location = cleanUrl(window.location.href);
      if (location === lastPage.current) return;

      const page = {
        page_location: location,
        page_path: window.location.pathname,
        page_referrer: previousPage.current || cleanUrl(document.referrer),
        page_title: document.title || 'Delps Transportes',
      };

      analytics.gtag?.('set', page);
      analytics.gtag?.('event', 'page_view', {
        ...page,
        send_to: MEASUREMENT_ID,
      });

      lastPage.current = location;
      previousPage.current = location;
    };

    trackPage();
    router.events.on('routeChangeComplete', trackPage);
    return () => router.events.off('routeChangeComplete', trackPage);
  }, [consent, router.isReady, router.events]);

  const choose = (value: Exclude<Consent, null>) => {
    const persisted = persistConsent(value);

    if (!persisted) {
      disableAnalytics();
      setConsent('rejected');
      setOpen(false);
      return;
    }

    if (value === 'rejected') {
      disableAnalytics();
    }

    setConsent(value);
    setOpen(false);
  };

  const closePreferences = () => {
    persistBannerDismissal();
    setOpen(false);
  };

  const togglePreferences = () => {
    if (open) {
      closePreferences();
    } else {
      setOpen(true);
    }
  };

  return {
    hydrated,
    open,
    scriptSrc:
      ENABLED && consent === 'accepted'
        ? `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
        : null,
    togglePreferences,
    closePreferences,
    acceptCookies: () => choose('accepted'),
    rejectCookies: () => choose('rejected'),
  };
}
